import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { cn, formatDogAge } from '@/shared/lib/utils';
import { Settings, Check, Plus, X, ArrowLeftRight } from 'lucide-react';
import { iconSize } from '@/shared/lib/design-tokens';
import { useThemeStore, getThemeStyles } from '@/shared/stores/theme-store';
import { DogAvatar } from '@/shared/components/common';
import type { Dog } from '@/shared/types/api.types';
interface DogHeaderProps {
  className?: string;
  dogName: string;
  dogImage: string;
  dogBirthDate?: string;
  dogId?: string;
  dogWeight?: number;
  dogs?: Dog[];
  onDogSelect?: (dog: Dog) => void;
}

export function DogHeader({
  className,
  dogName,
  dogImage,
  dogBirthDate,
  dogId,
  dogWeight,
  dogs = [],
  onDogSelect,
}: DogHeaderProps) {
  const navigate = useNavigate();
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const { getDogTheme } = useThemeStore();

  // Get current dog's theme and styles
  const theme = getDogTheme(dogId ?? '');
  const themeStyles = getThemeStyles(theme);

  // Handle modal open
  useEffect(() => {
    if (isSwitcherOpen) {
      setShouldRender(true);
      setIsClosing(false);
      document.body.style.overflow = 'hidden';
    }
  }, [isSwitcherOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Handle close with animation
  const handleCloseSwitcher = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
      setIsSwitcherOpen(false);
      document.body.style.overflow = '';
    }, 200);
  }, []);

  const handleDogClick = (dog: Dog) => {
    if (onDogSelect) {
      onDogSelect(dog);
    }
    handleCloseSwitcher();
  };

  return (
    <>
      <header
        className={cn(
          themeStyles.gradientClass,
          'fixed top-0 left-0 right-0 z-50 w-full',
          themeStyles.shadowClass,
          className
        )}
        style={{
          ...themeStyles.gradientStyle,
          ...themeStyles.shadowStyle,
        }}
      >
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          {/* Dog Avatar, Info and Swap - clickable to open dog switcher */}
          <button
            onClick={() => setIsSwitcherOpen(true)}
            className={cn(
              'flex items-center gap-3',
              'hover:opacity-90 transition-all duration-150',
              'active:scale-[0.98]',
              'touch-target'
            )}
            aria-label={dogs.length > 1 ? 'Trocar dog' : `Ver ${dogName}`}
          >
            <div className="relative">
              <DogAvatar
                src={dogImage}
                name={dogName}
                size="md"
                className="border-[3px] shadow-md"
                borderColor="rgba(255, 255, 255, 0.9)"
                fallbackBgColor="rgba(255, 255, 255, 0.2)"
                fallbackTextColor="#FFFFFF"
              />
              <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-400 border-2 border-white rounded-full shadow-sm" />
            </div>
            <div className="text-left">
              <h1 className="text-base font-semibold text-white drop-shadow-sm">{dogName}</h1>
              <p className="text-xs text-white/80 font-medium">
                {dogBirthDate ? formatDogAge(dogBirthDate) : 'Idade não informada'}
              </p>
              {dogWeight && (
                <p className="text-xs text-white/70 font-medium">
                  {dogWeight.toFixed(1)} kg
                </p>
              )}
            </div>
            {/* Swap Dog Icon - only show when multiple dogs */}
            {dogs.length > 1 && (
              <div className="p-1.5 text-white/80">
                <ArrowLeftRight size={iconSize.md} />
              </div>
            )}
          </button>

          <div className="flex items-center gap-1">
            {/* TODO: Habilitar quando sistema de notificações estiver pronto */}
            {/* <button
              onClick={() => void navigate({ to: '/notifications' })}
              className={cn(
                'relative p-2.5 text-white/80 rounded-xl',
                'transition-all duration-150',
                'hover:text-white hover:bg-white/15',
                'active:scale-95',
                'touch-target flex items-center justify-center'
              )}
            >
              <Bell size={iconSize.md} />
              <span
                className={cn(
                  'absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-amber-400 rounded-full border-2 shadow-sm',
                  theme.border.replace('border-', 'border-')
                )}
                style={{ borderColor: theme.preview }}
              />
            </button> */}
            <button
              onClick={() => void navigate({ to: '/settings' })}
              className={cn(
                'p-2.5 text-white/80 rounded-xl',
                'transition-all duration-150',
                'hover:text-white hover:bg-white/15',
                'active:scale-95',
                'touch-target flex items-center justify-center'
              )}
            >
              <Settings size={iconSize.md} />
            </button>
          </div>
        </div>
      </header>

      {/* Dog Switcher Modal */}
      {shouldRender && (
        <div
          className={cn(
            'fixed inset-0 z-[60] flex items-end sm:items-center justify-center'
          )}
        >
          {/* Backdrop */}
          <div
            className={cn(
              'absolute inset-0 bg-black/50 backdrop-blur-sm modal-backdrop',
              isClosing && 'closing'
            )}
            onClick={handleCloseSwitcher}
          />

          {/* Content */}
          <div
            className={cn(
              'relative bg-white w-full max-w-md',
              'rounded-t-3xl sm:rounded-3xl',
              'p-6 shadow-elevated',
              'modal-content',
              isClosing && 'closing'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Trocar Dog</h2>
                <p className="text-sm text-gray-500">Selecione um dog</p>
              </div>
              <button
                onClick={handleCloseSwitcher}
                className={cn(
                  'p-2.5 text-gray-400 rounded-xl bg-gray-100',
                  'transition-colors duration-150',
                  'hover:text-gray-600 hover:bg-gray-200',
                  'touch-target'
                )}
              >
                <X size={iconSize.sm} />
              </button>
            </div>

            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {dogs.map((dog) => {
                const isActive = dog.name === dogName;
                const themeColor = getDogTheme(dog.id);
                return (
                  <button
                    key={dog.id}
                    onClick={() => handleDogClick(dog)}
                    className={cn(
                      'w-full flex items-center gap-4 p-4 rounded-2xl',
                      'border-2 transition-all duration-200 text-left',
                      isActive
                        ? 'shadow-sm'
                        : 'border-gray-100 hover:bg-gray-50 hover:border-gray-200'
                    )}
                    style={
                      isActive
                        ? {
                            borderColor: themeColor.preview,
                            background: `linear-gradient(to right, ${themeColor.preview}10, ${themeColor.preview}15)`,
                          }
                        : undefined
                    }
                  >
                    <div className="relative">
                      <DogAvatar
                        key={`${dog.id}-${dog.avatarUrl ?? 'no-avatar'}`}
                        src={dog.avatarUrl}
                        name={dog.name}
                        size="lg"
                        borderColor={isActive ? themeColor.preview : '#E5E7EB'}
                        fallbackBgColor={isActive ? `${themeColor.preview}20` : '#F3F4F6'}
                        fallbackTextColor={isActive ? themeColor.preview : '#6B7280'}
                      />
                      {isActive && (
                        <div
                          className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center shadow-md"
                          style={{ backgroundColor: themeColor.preview }}
                        >
                          <Check size={14} className="text-white" strokeWidth={3} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{dog.name}</h3>
                        {isActive && (
                          <span
                            className="text-[10px] font-bold text-white px-2 py-0.5 rounded-full uppercase tracking-wide"
                            style={{ backgroundColor: themeColor.preview }}
                          >
                            Ativo
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-500">{dog.breed}</p>
                        {/* Show dog's theme color indicator */}
                        <div
                          className="w-3 h-3 rounded-full border border-white shadow-sm"
                          style={{ backgroundColor: themeColor.preview }}
                          title={themeColor.name}
                        />
                      </div>
                    </div>
                  </button>
                );
              })}

              <button
                onClick={() => {
                  handleCloseSwitcher();
                  setTimeout(() => void navigate({ to: '/settings/dog/add' }), 200);
                }}
                className={cn(
                  'w-full py-4 mt-3 border-2 border-dashed border-gray-300 rounded-2xl',
                  'text-gray-500 font-semibold flex items-center justify-center gap-2',
                  'transition-all duration-200'
                )}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = themeStyles.preview;
                  e.currentTarget.style.color = themeStyles.preview;
                  e.currentTarget.style.backgroundColor = `${themeStyles.preview}10`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#D1D5DB';
                  e.currentTarget.style.color = '#6B7280';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Plus size={iconSize.sm} />
                Adicionar Dog
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
